// =====================================================================
// EGL v12 Frontend - CERTIFICATE SERVICE
// Phase 3: Real-time validation and certificate management
// =====================================================================

import {
  Certificate,
  CertificateFormData,
  CertificateIdValidation,
  PublicCertificateResponse,
  CardDesign,
  ApiResponse,
  PaginatedResponse,
  CertificateSearchParams,
  GemstoneConfig,
  CERTIFICATE_CONSTANTS
} from '../types/certificate';

// API base URL - uses VITE_API_BASE_URL from environment or falls back to relative URLs
// In development: VITE_API_BASE_URL is set in docker-compose.yml
// In production: Use empty string for relative URLs (served from same domain)
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

// Certificate Service Class
export class CertificateService {
  private static instance: CertificateService;
  private validationCache = new Map<string, CertificateIdValidation>();
  private validationTimeouts = new Map<string, NodeJS.Timeout>();

  private constructor() {}

  public static getInstance(): CertificateService {
    if (!CertificateService.instance) {
      CertificateService.instance = new CertificateService();
    }
    return CertificateService.instance;
  }

  // =====================================================================
  // CERTIFICATE ID VALIDATION (Phase 2 Integration)
  // =====================================================================

  /**
   * Real-time Certificate ID validation with debouncing
   * @param certId Certificate ID to validate
   * @param debounceMs Debounce delay in milliseconds (default: 300ms)
   * @returns Promise with validation result
   */
  public async validateCertificateId(
    certId: string, 
    debounceMs: number = 300
  ): Promise<CertificateIdValidation> {
    // Clear existing timeout for this cert_id
    const existingTimeout = this.validationTimeouts.get(certId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    // Return cached result if available and recent
    const cached = this.validationCache.get(certId);
    if (cached && Date.now() - (cached as any).timestamp < 30000) { // 30 second cache
      return cached;
    }

    // Client-side format validation first
    const formatValid = CERTIFICATE_CONSTANTS.CERT_ID_PATTERN.test(certId);
    if (!formatValid) {
      const result: CertificateIdValidation = {
        valid: false,
        available: false,
        message: "Certificate ID must contain only uppercase letters and numbers (A-Z, 0-9), max 20 characters, no spaces",
        format_valid: false,
        duplicate_check: false,
        cert_id: certId
      };
      this.validationCache.set(certId, { ...result, timestamp: Date.now() } as any);
      return result;
    }

    // Return promise that resolves after debounce
    return new Promise((resolve) => {
      const timeout = setTimeout(async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/api/certificates/validate-cert-id/${certId}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          });

          const result: CertificateIdValidation = await response.json();
          
          // Cache the result
          this.validationCache.set(certId, { ...result, timestamp: Date.now() } as any);
          
          resolve(result);
        } catch (error) {
          console.error('Certificate ID validation error:', error);
          const errorResult: CertificateIdValidation = {
            valid: false,
            available: false,
            message: "Error validating Certificate ID - please try again",
            format_valid: formatValid,
            duplicate_check: false,
            cert_id: certId
          };
          resolve(errorResult);
        } finally {
          this.validationTimeouts.delete(certId);
        }
      }, debounceMs);

      this.validationTimeouts.set(certId, timeout);
    });
  }

  /**
   * Clear validation cache (useful for testing or manual refresh)
   */
  public clearValidationCache(): void {
    this.validationCache.clear();
    this.validationTimeouts.forEach(timeout => clearTimeout(timeout));
    this.validationTimeouts.clear();
  }

  // =====================================================================
  // GEMSTONE CONFIGURATION MANAGEMENT
  // =====================================================================

  /**
   * Fetch gemstone measurement configurations
   * @returns Promise with gemstone configurations
   */
  public async getGemstoneConfigurations(): Promise<ApiResponse<GemstoneConfig[]>> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/certificates/gemstone-configs`);
      const result: ApiResponse<GemstoneConfig[]> = await response.json();
      
      if (!response.ok) {
        throw new Error(result.message || 'Failed to fetch gemstone configurations');
      }
      
      return result;
    } catch (error) {
      console.error('Error fetching gemstone configurations:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error occurred',
        data: []
      };
    }
  }

  // =====================================================================
  // CERTIFICATE MANAGEMENT
  // =====================================================================

  /**
   * Create a new certificate
   * @param formData Certificate form data
   * @param authToken JWT authentication token
   * @returns Promise with created certificate including UURLID
   */
  public async createCertificate(
    formData: CertificateFormData,
    authToken: string
  ): Promise<ApiResponse<Certificate>> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/certificates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify(formData),
      });

      const result: ApiResponse<Certificate> = await response.json();
      
      // Clear validation cache for this cert_id since it's now taken
      if (result.success && result.data?.cert_id) {
        this.validationCache.delete(result.data.cert_id);
      }
      
      return result;
    } catch (error) {
      console.error('Certificate creation error:', error);
      return {
        success: false,
        message: "Error creating certificate - please try again",
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Get certificates with search and pagination
   * @param params Search and pagination parameters
   * @param authToken JWT authentication token
   * @returns Promise with paginated certificate results
   */
  public async getCertificates(
    params: CertificateSearchParams = {},
    authToken: string
  ): Promise<PaginatedResponse<Certificate>> {
    try {
      const queryParams = new URLSearchParams();
      
      // Add all parameters to query string
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, String(value));
        }
      });

      const response = await fetch(`${API_BASE_URL}/api/certificates?${queryParams}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
      });

      return await response.json();
    } catch (error) {
      console.error('Get certificates error:', error);
      return {
        success: false,
        message: "Error fetching certificates - please try again",
        data: [],
        pagination: { page: 1, limit: 20, total: 0, pages: 0 },
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Update an existing certificate
   * @param certificateId Certificate ID to update
   * @param updateData Partial certificate data to update
   * @param authToken JWT authentication token
   * @returns Promise with updated certificate
   */
  public async updateCertificate(
    certificateId: string,
    updateData: Partial<CertificateFormData>,
    authToken: string
  ): Promise<ApiResponse<Certificate>> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/certificates/${certificateId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify(updateData),
      });

      return await response.json();
    } catch (error) {
      console.error('Certificate update error:', error);
      return {
        success: false,
        message: "Error updating certificate - please try again",
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  // =====================================================================
  // PUBLIC CERTIFICATE LOOKUP (Phase 2 Integration)
  // =====================================================================

  /**
   * Get public certificate by UURLID (no authentication required)
   * @param uurlid 8-character UURLID
   * @returns Promise with public certificate data
   */
  public async getPublicCertificate(uurlid: string): Promise<PublicCertificateResponse> {
    try {
      // Validate UURLID format
      if (!CERTIFICATE_CONSTANTS.UURLID_PATTERN.test(uurlid)) {
        return {
          success: false,
          message: "Invalid certificate URL format",
          error: "INVALID_UURLID_FORMAT"
        };
      }

      const response = await fetch(`${API_BASE_URL}/api/certificates/public/certificate/${uurlid}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return await response.json();
    } catch (error) {
      console.error('Public certificate lookup error:', error);
      return {
        success: false,
        message: "Error retrieving certificate - please try again",
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Generate public URL from UURLID
   * @param uurlid 8-character UURLID
   * @returns Public certificate URL
   */
  public generatePublicUrl(uurlid: string): string {
    return `https://qr.eugemlab.com/${uurlid}`;
  }

  // =====================================================================
  // CARD DESIGN MANAGEMENT
  // =====================================================================

  /**
   * Get available card designs
   * @param authToken JWT authentication token (optional for public designs)
   * @returns Promise with card design list
   */
  public async getCardDesigns(authToken?: string): Promise<ApiResponse<CardDesign[]>> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      const response = await fetch(`${API_BASE_URL}/api/card-designs`, {
        method: 'GET',
        headers,
      });

      return await response.json();
    } catch (error) {
      console.error('Get card designs error:', error);
      return {
        success: false,
        message: "Error fetching card designs - please try again",
        data: [],
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  // =====================================================================
  // UTILITY METHODS
  // =====================================================================

  /**
   * Validate form data before submission
   * @param formData Certificate form data to validate
   * @returns Validation result with errors
   */
  public validateFormData(formData: Partial<CertificateFormData>): {
    valid: boolean;
    errors: Record<string, string>;
  } {
    const errors: Record<string, string> = {};

    // Required field validation
    if (!formData.cert_id) {
      errors.cert_id = "Certificate ID is required";
    } else if (!CERTIFICATE_CONSTANTS.CERT_ID_PATTERN.test(formData.cert_id)) {
      errors.cert_id = "Certificate ID must contain only uppercase letters and numbers (A-Z, 0-9), max 15 characters";
    }

    if (!formData.gemstone_type) {
      errors.gemstone_type = "Gemstone type is required";
    }

    if (!formData.gemstone_weight || formData.gemstone_weight <= 0) {
      errors.gemstone_weight = "Gemstone weight must be greater than 0";
    }

    if (!formData.metal_composition) {
      errors.metal_composition = "Metal composition is required";
    }

    if (!formData.total_weight || formData.total_weight <= 0) {
      errors.total_weight = "Total weight must be greater than 0";
    }

    if (!formData.gemstone_growth_origin) {
      errors.gemstone_growth_origin = "Gemstone growth origin is required";
    }

    if (!formData.card_design_code) {
      errors.card_design_code = "Card design is required";
    }

    // Comment line validation (ASCII only, max 20 chars)
    for (let i = 1; i <= 3; i++) {
      const commentKey = `comment_line_${i}` as keyof CertificateFormData;
      const comment = formData[commentKey] as string;
      if (comment && comment.length > CERTIFICATE_CONSTANTS.COMMENT_MAX_LENGTH) {
        errors[commentKey] = `Comment line ${i} cannot exceed ${CERTIFICATE_CONSTANTS.COMMENT_MAX_LENGTH} characters`;
      }
      if (comment && !CERTIFICATE_CONSTANTS.ASCII_PATTERN.test(comment)) {
        errors[commentKey] = `Comment line ${i} must contain only printable ASCII characters`;
      }
    }

    // Abbreviation line validation (ASCII only, max 20 chars)
    for (let i = 1; i <= 13; i++) {
      const abbrevKey = `abbreviation_line_${i}` as keyof CertificateFormData;
      const abbrev = formData[abbrevKey] as string;
      if (abbrev && abbrev.length > CERTIFICATE_CONSTANTS.ABBREVIATION_MAX_LENGTH) {
        errors[abbrevKey] = `Abbreviation line ${i} cannot exceed ${CERTIFICATE_CONSTANTS.ABBREVIATION_MAX_LENGTH} characters`;
      }
      if (abbrev && !CERTIFICATE_CONSTANTS.ASCII_PATTERN.test(abbrev)) {
        errors[abbrevKey] = `Abbreviation line ${i} must contain only printable ASCII characters`;
      }
    }

    return {
      valid: Object.keys(errors).length === 0,
      errors
    };
  }

  /**
   * Format weight for display (ensures 2 decimal places)
   * @param weight Weight value
   * @returns Formatted weight string
   */
  public formatWeight(weight: number): string {
    return weight.toFixed(2);
  }

  /**
   * Parse weight from string input (handles user input)
   * @param weightStr Weight as string
   * @returns Parsed weight number
   */
  public parseWeight(weightStr: string): number {
    const parsed = parseFloat(weightStr);
    return isNaN(parsed) ? 0 : Math.round(parsed * 100) / 100; // Round to 2 decimal places
  }
}

// Export singleton instance
export const certificateService = CertificateService.getInstance();

// Export class for testing
export default CertificateService; 